import React, { useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  ModalBox,
  Radio,
  RadioGroup,
  Typography,
} from 'components';

import { useTranslation } from 'third-party';

export type ExportFormat = 'Excel' | 'Csv';

export type PaymentExportOptions = {
  format: ExportFormat;
  columns: string[] | null;
  exportPageSize: number;
  includeTotalsRow: boolean;
};

type ColumnDef = { name: string; labelKey: string };
type ColumnGroup = { labelKey: string; columns: ColumnDef[] };

const COLUMN_GROUPS: ColumnGroup[] = [
  {
    labelKey: 'exportModal.group.paymentInfo',
    columns: [
      { name: 'Amount', labelKey: 'paymentList.column.amount' },
      { name: 'CheckDate', labelKey: 'paymentList.column.checkDate' },
      { name: 'CheckNumber', labelKey: 'paymentList.column.checkNumber' },
      { name: 'Status', labelKey: 'paymentList.column.status' },
      { name: 'Note', labelKey: 'paymentList.column.note' },
      { name: 'Memo', labelKey: 'paymentList.column.memo' },
      { name: 'Name', labelKey: 'paymentList.column.name' },
      { name: 'Company', labelKey: 'paymentList.column.company' },
      { name: 'ReferenceId', labelKey: 'paymentList.column.referenceId' },
      { name: 'VerifyCode', labelKey: 'paymentList.column.verifyCode' },
      { name: 'KId', labelKey: 'paymentList.column.kId' },
      { name: 'ECheck', labelKey: 'exportModal.column.eCheck' },
    ],
  },
  {
    labelKey: 'exportModal.group.audit',
    columns: [
      { name: 'Id', labelKey: 'paymentList.column.id' },
      { name: 'CreatedOn', labelKey: 'paymentList.column.createdOn' },
      { name: 'UpdatedOn', labelKey: 'paymentList.column.updatedOn' },
      { name: 'DeliveryTime', labelKey: 'paymentList.column.deliveryTime' },
      { name: 'EPrintCount', labelKey: 'exportModal.column.ePrintCount' },
    ],
  },
];

const ALL_COLUMN_NAMES = COLUMN_GROUPS.flatMap(its =>
  its.columns.map(it => it.name),
);

const PAGE_SIZE_OPTIONS = [
  { value: 500, label: '500' },
  { value: 1000, label: '1,000' },
  { value: 5000, label: '5,000' },
  { value: -1, labelKey: 'exportModal.pageSize.all' },
];

type Props = {
  onClose: () => void;
  onExport: (options: PaymentExportOptions) => void;
  isPending: boolean;
};

export const PaymentExportModal: React.FC<Props> = ({
  onClose,
  onExport,
  isPending,
}) => {
  const { t } = useTranslation();

  const [format, setFormat] = useState<ExportFormat>('Excel');
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(
    new Set(ALL_COLUMN_NAMES),
  );
  const [pageSize, setPageSize] = useState(1000);
  const [includeTotalsRow, setIncludeTotalsRow] = useState(false);

  const allSelected = selectedColumns.size === ALL_COLUMN_NAMES.length;

  const toggleColumn = (name: string) => {
    setSelectedColumns(its => {
      const next = new Set(its);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const toggleGroup = (group: ColumnGroup) => {
    const names = group.columns.map(it => it.name);
    const allGroupSelected = names.every(it => selectedColumns.has(it));
    setSelectedColumns(its => {
      const next = new Set(its);
      if (allGroupSelected) {
        names.forEach(it => next.delete(it));
      } else {
        names.forEach(it => next.add(it));
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedColumns(allSelected ? new Set() : new Set(ALL_COLUMN_NAMES));
  };

  const handleExport = () => {
    onExport({
      format,
      columns: allSelected ? null : Array.from(selectedColumns),
      exportPageSize: pageSize === -1 ? 5000 : pageSize,
      includeTotalsRow,
    });
  };

  return (
    <ModalBox width="35rem" height="36rem" onClose={onClose}>
      <Box px="1.5rem" pb="1.5rem">
        <Box
          position="sticky"
          top={0}
          zIndex={10}
          bgcolor="white"
          pt="1.5rem"
          pb="0.5rem"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap="0.5rem"
          >
            <Box overflow="hidden">
              <Typography variant="h2" noWrap>
                {t('exportModal.title')}
              </Typography>
              <Typography variant="subtitle1">
                {t('exportModal.subTitle')}
              </Typography>
            </Box>
            <Box display="flex" columnGap="1rem" flexShrink={0}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleExport}
                disabled={isPending || selectedColumns.size === 0}
              >
                {isPending
                  ? t('common.loading')
                  : t('exportModal.exportButton')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={onClose}
                disabled={isPending}
              >
                {t('common.form.cancel')}
              </Button>
            </Box>
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap="1rem" mt="1rem">
          <FormControl>
            <FormLabel>{t('exportModal.format')}</FormLabel>
            <RadioGroup
              row
              value={format}
              onChange={e => setFormat(e.target.value as ExportFormat)}
            >
              <FormControlLabel
                value="Excel"
                control={<Radio size="small" />}
                label="Excel (.xlsx)"
              />
              <FormControlLabel
                value="Csv"
                control={<Radio size="small" />}
                label="CSV"
              />
            </RadioGroup>
          </FormControl>

          <Divider />

          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb="0.25rem"
            >
              <FormLabel>{t('exportModal.columns')}</FormLabel>
              <Button size="small" variant="text" onClick={toggleAll}>
                {allSelected
                  ? t('exportModal.deselectAll')
                  : t('exportModal.selectAll')}
              </Button>
            </Box>
            <Box display="flex" gap="1rem" flexWrap="wrap">
              {COLUMN_GROUPS.map(group => {
                const names = group.columns.map(c => c.name);
                const checkedCount = names.filter(n =>
                  selectedColumns.has(n),
                ).length;
                const allGroupSelected = checkedCount === names.length;
                const someGroupSelected = checkedCount > 0 && !allGroupSelected;

                return (
                  <FormControl
                    key={group.labelKey}
                    component="fieldset"
                    sx={{ minWidth: '12rem' }}
                  >
                    <FormControlLabel
                      label={
                        <Typography variant="body2" fontWeight="bold">
                          {t(group.labelKey as any)}
                        </Typography>
                      }
                      control={
                        <Checkbox
                          size="small"
                          checked={allGroupSelected}
                          indeterminate={someGroupSelected}
                          onChange={() => toggleGroup(group)}
                        />
                      }
                    />
                    <FormGroup sx={{ pl: '1rem' }}>
                      {group.columns.map(col => (
                        <FormControlLabel
                          key={col.name}
                          label={
                            <Typography variant="body2">
                              {t(col.labelKey as any)}
                            </Typography>
                          }
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedColumns.has(col.name)}
                              onChange={() => toggleColumn(col.name)}
                            />
                          }
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                );
              })}
            </Box>
          </Box>

          <Divider />

          <FormControl>
            <FormLabel>{t('exportModal.recordLimit')}</FormLabel>
            <RadioGroup
              row
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map(opt => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">
                      {opt.labelKey ? t(opt.labelKey as any) : opt.label}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          <Divider />

          <FormControlLabel
            sx={{ mb: '1rem' }}
            control={
              <Checkbox
                size="small"
                checked={includeTotalsRow}
                onChange={e => setIncludeTotalsRow(e.target.checked)}
              />
            }
            label={t('exportModal.includeTotals')}
          />
        </Box>
      </Box>
    </ModalBox>
  );
};
